/**
 * La schermata d'ingresso: un codice, non un modulo.
 *
 * **È l'unica schermata che non può portare il marchio dell'operatore**, e va disegnata sapendolo
 * (01-ARCHITETTURA §5): prima dell'accoppiamento l'app non ha ancora parlato con nessun gateway,
 * quindi non ha nome, né logo, né palette, e la lingua può prenderla solo dal sistema.
 *
 * Ha sostituito la schermata di accesso con host, utente e password. Il motivo è il gesto, non la
 * grafica: scrivere un token di 128 caratteri col telecomando è il modo in cui si perde un cliente
 * prima ancora di mostrargli un canale. La conseguenza accettata — l'utente finale non può più
 * attivarsi da solo, gli serve un rivenditore raggiungibile — sta in SPEC §9.
 *
 * **Non conta il tempo da sé.** `remainingSeconds` arriva da chi la ospita, che lo calcola sul
 * numero dato dal servizio: i televisori hanno l'orologio sbagliato, spesso di anni, e un conto
 * alla rovescia locale mostra «ancora 40 secondi» su una richiesta già morta.
 */

import { useRef, type ReactNode } from 'react';
import { formatPairingCode, tailOf, type GatewayError } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors, layout } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { useSpatialFocus } from '../useSpatialFocus.js';
import { QrCode } from '../components/QrCode.js';

export type PairingStatus =
  /** Si sta chiedendo un codice. Dura un attimo, ma un attimo muto sembra un'app bloccata. */
  | { readonly kind: 'requesting' }
  | { readonly kind: 'showing'; readonly code: string; readonly remainingSeconds: number }
  /** Il codice è morto: non si interroga più, si chiede il prossimo. */
  | { readonly kind: 'expired' }
  | { readonly kind: 'failed'; readonly error: GatewayError };

export interface PairingScreenProps {
  readonly focusPort: FocusPort;
  readonly deviceId: string;
  readonly appVersion: string;
  readonly status: PairingStatus;
  /**
   * Il rifiuto della linea che c'era prima, quando si torna qui da una sessione.
   *
   * Si mostra **insieme** al codice nuovo, non al posto suo: chi vede ricomparire l'attivazione
   * senza una parola non sa se è scaduto l'abbonamento, se l'ha disattivato il rivenditore, o se
   * l'apparecchio si è dimenticato di lui.
   */
  readonly notice?: GatewayError | null;
  /**
   * Le credenziali sono arrivate e si sta entrando.
   *
   * Copre il codice invece di lasciarlo a schermo col conto alla rovescia che scende: quel codice è
   * **già stato consumato** — il servizio lo cancella consegnandolo — e continuare a mostrarlo
   * invita il rivenditore a riprovare con un codice che non esiste più.
   */
  readonly entering?: boolean;
  /** Chiede un codice nuovo. È l'unica azione della schermata. */
  readonly onRenew: () => void;
}

/** Quanto è grande il QR sul disegno a 1920×1080. */
const QR_SIZE_PX = 380;

/**
 * Sotto questa soglia il conto alla rovescia diventa rosso.
 *
 * Quindici secondi, cioè il tempo di dire «rifallo» a voce e premere un tasto: prima non serve
 * allarmare nessuno, dopo non serve più a niente.
 */
const URGENT_SECONDS = 15;

export function PairingScreen({
  focusPort,
  deviceId,
  appVersion,
  status,
  notice = null,
  entering = false,
  onRenew,
}: PairingScreenProps): ReactNode {
  const translator = useTranslator();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useSpatialFocus(focusPort, containerRef);

  // Il rifiuto della linea precedente ha la precedenza su un guasto dell'accoppiamento: è il motivo
  // per cui chi guarda è tornato qui, e l'altro è una conseguenza di questo giro.
  const problem: GatewayError | null = notice ?? (status.kind === 'failed' ? status.error : null);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        width: layout.designWidth,
        height: layout.designHeight,
        background: colors.background,
        color: colors.text,
      }}
    >
      <aside
        style={{
          flex: 'none',
          width: layout.loginAsideWidth,
          padding: '96px 72px 64px',
          display: 'flex',
          flexDirection: 'column',
          gap: 26,
          background: colors.panel,
          borderRight: `1px solid ${colors.borderFaint}`,
        }}
      >
        {/* Nessun marchio: l'app mostra solo sé stessa finché non sa a chi appartiene. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            font: `800 96px/1 ${FONT_STACK}`,
            letterSpacing: '-.04em',
          }}
        >
          {translator.t('common.appName')}
          <span style={{ width: 22, height: 22, borderRadius: 11, background: colors.accent, display: 'inline-block' }} />
        </div>

        <div style={{ font: `700 44px/1.1 ${FONT_STACK}`, letterSpacing: '-.02em', paddingTop: 24 }}>
          {translator.t('pairing.title')}
        </div>
        <div style={{ font: `400 25px/1.45 ${FONT_STACK}`, color: colors.textMuted }}>
          {translator.t('pairing.subtitle')}
        </div>

        {/* Le tre cose da fare, nell'ordine in cui si fanno. */}
        <ol style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 18, margin: 0, listStyle: 'none' }}>
          {(['pairing.steps.call', 'pairing.steps.read', 'pairing.steps.wait'] as const).map((key, index) => (
            <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span
                style={{
                  flex: 'none',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: `2px solid ${colors.border}`,
                  color: colors.textFaint,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  font: `800 18px ${FONT_STACK}`,
                }}
              >
                {index + 1}
              </span>
              <span style={{ font: `500 24px ${FONT_STACK}`, color: colors.textMuted }}>
                {translator.t(key)}
              </span>
            </li>
          ))}
        </ol>

        {problem !== null ? (
          <div
            style={{
              background: colors.errorPanel,
              border: `1px solid ${colors.live}`,
              borderRadius: 14,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <span style={{ font: `700 22px ${FONT_STACK}`, color: colors.errorText }}>
              {translator.t(notice !== null ? 'pairing.noticeTitle' : 'pairing.errorTitle')}
            </span>
            <span style={{ font: `500 22px/1.4 ${FONT_STACK}` }}>
              {translator.t(`errors.${problem.code}`, problem.params)}
            </span>
            {/* Il codice e l'ora, in piccolo. Non per chi guarda: per chi risponde al telefono. */}
            <span style={{ font: '500 16px ui-monospace, Menlo, monospace', color: colors.textFaint }}>
              {translator.t('common.errorCodeAt', {
                code: problem.code,
                time: translator.time(problem.atMs),
              })}
            </span>
          </div>
        ) : null}

        <div style={{ marginTop: 'auto', font: `500 19px/1.5 ${FONT_STACK}`, color: colors.textFaint }}>
          {translator.t('device.line', { deviceId: tailOf(deviceId, 9), version: appVersion })}
        </div>
      </aside>

      <main
        data-main="1"
        style={{
          flex: 1,
          minWidth: 0,
          padding: '96px 72px 64px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 32,
        }}
      >
        {entering ? (
          <div style={{ font: `600 32px ${FONT_STACK}`, color: colors.textMuted }}>
            {translator.t('pairing.entering')}
          </div>
        ) : status.kind === 'showing' ? (
          <>
            <QrCode text={status.code} sizePx={QR_SIZE_PX} />

            {/*
              Il codice a caratteri, grande. Non è un ripiego per quando il QR non si legge: è la
              strada che si usa al telefono, dove nessuno può inquadrare niente.
            */}
            <div
              data-testid="pairing-code"
              style={{
                font: `800 92px/1 ui-monospace, Menlo, monospace`,
                letterSpacing: '0.12em',
                color: colors.text,
              }}
            >
              {formatPairingCode(status.code)}
            </div>

            <div
              data-testid="pairing-countdown"
              style={{
                font: `600 30px ${FONT_STACK}`,
                color: status.remainingSeconds <= URGENT_SECONDS ? colors.live : colors.textMuted,
              }}
            >
              {translator.t('pairing.expiresIn', { count: status.remainingSeconds })}
            </div>

            <div style={{ font: `400 24px/1.5 ${FONT_STACK}`, color: colors.textFaint, textAlign: 'center', maxWidth: 720 }}>
              {translator.t('pairing.scanHint')}
            </div>
          </>
        ) : status.kind === 'requesting' ? (
          <div style={{ font: `600 32px ${FONT_STACK}`, color: colors.textMuted }}>
            {translator.t('pairing.requesting')}
          </div>
        ) : (
          /*
            Scaduta o fallita: la stessa schermata, un tasto solo. Arrivati qui **non si interroga
            più** — un codice morto consuma il limite di frequenza per niente, e un conto alla
            rovescia fermo a zero è indistinguibile da un guasto.
          */
          <>
            <div style={{ font: `600 32px/1.4 ${FONT_STACK}`, color: colors.textMuted, textAlign: 'center', maxWidth: 760 }}>
              {translator.t(status.kind === 'expired' ? 'pairing.expired' : 'pairing.failedBody')}
            </div>
            <button
              type="button"
              data-f="1"
              data-testid="pairing-renew"
              onClick={onRenew}
              style={{
                height: 72,
                padding: '0 44px',
                borderRadius: 36,
                border: `2px solid ${colors.accent}`,
                background: colors.accent,
                color: colors.background,
                font: `700 28px ${FONT_STACK}`,
                cursor: 'pointer',
              }}
            >
              {translator.t('pairing.renew')}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
